/**
 * Translation files for multilingual support
  * Currently supports: English (en), Slovenian (sl), German (de)
 */

export const translations = {
  en: {
    // Header
    header: {
      myGarages: 'My Garages',
      notifications: 'Notifications',
      myProfile: 'My Profile',
      listYourGarage: 'List Your Garage',
      myBookings: 'My Bookings',
      reportBug: 'Report a Bug',
      logout: 'Logout',
      login: 'Login',
      signUp: 'Sign Up',
      changeTheme: 'Change Theme',
      theme: 'Theme',
      changeLanguage: 'Change Language'
    },
    
    // Home/GarageList
    home: {
      title: 'Find Your Perfect Parking Space',
      subtitle: 'Discover and book secure garage spaces near you',
      searchPlaceholder: 'Search by location...',
      filterAll: 'All',
      priceRange: 'Price Range',
      features: 'Features',
      priceFrom: 'From',
      priceTo: 'To',
      applyFilters: 'Apply Filters',
      resetFilters: 'Reset Filters',
      perHour: '/hour',
      perDay: '/day',
      perMonth: '/month',
      available: 'Available',
      viewDetails: 'View Details',
      noGarages: 'No garages found',
      noGaragesDesc: 'Try adjusting your filters or search in a different location.',
      loading: 'Loading garages...',
    },
    
    // Garage Details
    garageDetail: {
      bookNow: 'Book Now',
      overview: 'Overview',
      location: 'Location',
      pricing: 'Pricing',
      features: 'Features',
      availability: 'Availability',
      hourly: 'Hourly',
      daily: 'Daily',
      monthly: 'Monthly',
      contactOwner: 'Contact Owner',
      unavailable: 'Currently Unavailable',
      selectDates: 'Select your dates to book this garage',
      startDate: 'Start Date',
      endDate: 'End Date',
      bookingType: 'Booking Type',
      totalPrice: 'Total Price',
      confirmBooking: 'Confirm Booking',
      cancel: 'Cancel',
      bookingSuccess: 'Booking request sent successfully!',
      bookingError: 'Failed to create booking',
    },
    
    // Login
    login: {
      title: 'Welcome Back',
      subtitle: 'Login to your account',
      email: 'Email Address',
      password: 'Password',
      forgotPassword: 'Forgot Password?',
      loginButton: 'Login',
      noAccount: "Don't have an account?",
      signUpLink: 'Sign Up',
      error: 'Invalid credentials',
    },
    
    // Register
    register: {
      title: 'Create Account',
      subtitle: 'Join GarageShare today',
      name: 'Full Name',
      email: 'Email Address',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      role: 'I want to',
      roleRenter: 'Rent a garage',
      roleOwner: 'List my garage',
      signUpButton: 'Sign Up',
      hasAccount: 'Already have an account?',
      loginLink: 'Login',
      passwordMismatch: 'Passwords do not match',
      error: 'Registration failed',
    },
    
    // Profile
    profile: {
      title: 'My Profile',
      personalInfo: 'Personal Information',
      accountSettings: 'Account Settings',
      name: 'Full Name',
      fullName: 'Full Name',
      email: 'Email Address',
      phone: 'Phone Number',
      role: 'Account Type',
      renter: 'Renter',
      owner: 'Owner',
      profilePicture: 'Profile Picture',
      changePhoto: 'Change Photo',
      removePhoto: 'Remove Photo',
      uploadPhoto: 'Upload Photo',
      remove: 'Remove',
      saveChanges: 'Save Changes',
      edit: 'Edit Profile',
      cancel: 'Cancel',
      updateSuccess: 'Profile updated successfully!',
      updateError: 'Failed to update profile',
      nameEmailRequired: 'Name and email are required',
      invalidEmail: 'Please enter a valid email address',
      selectImageFile: 'Please select an image file',
      imageMaxSize: 'Image size must be less than 5MB',
      profilePictureUpdated: 'Profile picture updated!',
      profilePictureRemoved: 'Profile picture removed',
      accountInfo: 'Account Information',
      subtitle: 'Manage your account information',
      notSet: 'Not set',
      memberSince: 'Member Since',
      userId: 'User ID',
      saving: 'Saving...',
      // Password Section
      changePassword: 'Change Password',
      currentPassword: 'Current Password',
      newPassword: 'New Password',
      confirmPassword: 'Confirm New Password',
      allPasswordFieldsRequired: 'All password fields are required',
      passwordMinLength: 'New password must be at least 6 characters',
      passwordMismatch: 'Passwords do not match',
      passwordChangeSuccess: 'Password changed successfully!',
      passwordChangeError: 'Failed to change password',
      changing: 'Changing...',
      passwordSecurityTip: 'Keep your account secure by using a strong password',
      passwordTip1: 'Use at least 6 characters',
      passwordTip2: 'Mix uppercase and lowercase letters',
      passwordTip3: 'Include numbers and special characters',
      passwordPlaceholder: 'At least 6 characters',
      // Statistics
      totalBookings: 'Total Bookings',
      listedGarages: 'Listed Garages',
      totalEarnings: 'Total Earnings',
      // Delete Account
      dangerZone: 'Danger Zone',
      deleteAccount: 'Delete Account',
      deleteAccountWarning: 'Once you delete your account, there is no going back. Please be certain.',
      deleteAccountConfirm: 'Are you sure you want to delete your account? This action cannot be undone.',
      deleteAccountSubtext: 'All your data, including bookings and listings, will be permanently deleted.',
      enterPasswordConfirm: 'Enter your password to confirm',
      deleting: 'Deleting...',
      yesDeleteAccount: 'Yes, Delete My Account',
      accountDeleted: 'Account deleted successfully. Redirecting...',
      deleteAccountError: 'Failed to delete account',
      enterPasswordToConfirm: 'Please enter your password to confirm',
      // Language Settings
      language: 'Language',
      languageSubtitle: 'Choose your preferred language',
    },
    
    // Add Garage
    addGarage: {
      title: 'List Your Garage',
      subtitle: 'Share your garage space and earn money',
      basicInfo: 'Basic Information',
      garageName: 'Garage Name',
      namePlaceholder: 'e.g., Downtown Secure Parking',
      description: 'Description',
      descriptionPlaceholder: 'Describe your garage space, location benefits, and any special features...',
      location: 'Location',
      address: 'Street Address',
      addressPlaceholder: '123 Main Street',
      city: 'City',
      state: 'State',
      postalCode: 'Zip Code',
      country: 'Country',
      pricing: 'Pricing & Size',
      capacity: 'Capacity',
      hourlyRate: 'Hourly Rate (€)',
      dailyRate: 'Daily Rate (€)',
      monthlyRate: 'Monthly Rate (€)',
      features: 'Features',
      selectFeatures: 'Select all that apply',
      covered: 'Covered',
      security: '24/7 Security',
      cctv: 'CCTV',
      electricCharging: 'Electric Charging',
      carWash: 'Car Wash',
      lighting: 'Lighting',
      images: 'Photos',
      uploadImages: 'Upload photos of your garage (required, max 5MB each)',
      clickUpload: 'Click to upload images',
      dragDrop: 'PNG, JPG, GIF up to 5MB',
      availability: 'Availability',
      availableNow: 'Available Now',
      cancelButton: 'Cancel',
      submitButton: 'List My Garage',
      updateButton: 'Update Garage',
      createSuccess: 'Garage listed successfully!',
      updateSuccess: 'Garage updated successfully!',
      createError: 'Failed to list garage. Please try again.',
      fillRequired: 'Please fill in all required fields',
      priceGreaterThanZero: 'Price must be greater than 0',
      uploadOneImage: 'Please upload at least one image',
      uploadingImages: 'Uploading images...',
      savingDetails: 'Saving garage details...',
      uploadImageError: 'Failed to upload image',
      listing: 'Listing Garage...',
    },
    
    // Edit Garage
    editGarage: {
      title: 'Edit Your Garage',
      subtitle: 'Update your garage listing information',
      loadingData: 'Loading garage data...',
      notFound: 'Garage not found',
      noPermission: 'You do not have permission to edit this garage',
      failedLoad: 'Failed to load garage data',
      updateSuccess: 'Garage updated successfully!',
      updateError: 'Failed to update garage. Please try again.',
      uploadingImages: 'Uploading new images...',
      updatingDetails: 'Updating garage details...',
      updating: 'Updating Garage...',
      imageError: 'Please select only image files',
      imageSizeError: 'Images must be less than 5MB each',
    },
    
    // My Garages
    myGarages: {
      title: 'My Garages',
      subtitle: 'Manage your garage listings',
      addNew: 'Add New Garage',
      noGarages: 'No Garages Listed Yet',
      noGaragesDesc: 'Start earning by listing your first garage space!',
      listFirst: 'List Your First Garage',
      edit: 'Edit',
      delete: 'Delete',
      deleteConfirm: 'Delete Garage',
      deleteWarning: 'Are you sure you want to delete',
      deleteSubtext: 'This action cannot be undone. All booking history and data for this garage will be permanently removed.',
      deleteSuccess: 'Garage deleted successfully!',
      deleteError: 'Failed to delete garage',
      availabilitySuccess: 'Availability updated successfully!',
      availabilityError: 'Failed to update availability',
      loadingGarages: 'Loading your garages...',
      available: 'Available',
      unavailable: 'Unavailable',
      active: 'Active',
      inactive: 'Inactive',
      noImage: 'No Image',
      price: 'Price',
      size: 'Size',
      features: 'Features',
      moreFeatures: 'more',
      views: 'Views',
      bookings: 'Bookings',
      rating: 'Rating',
      cancel: 'Cancel',
      confirmDelete: 'Yes, Delete Garage',
    },
    
    // My Bookings
    myBookings: {
      title: 'My Bookings',
      viewAll: 'All',
      viewPending: 'Pending',
      viewConfirmed: 'Confirmed',
      viewCompleted: 'Completed',
      viewCancelled: 'Cancelled',
      filterAll: 'All',
      filterUpcoming: 'Upcoming',
      filterPast: 'Past',
      noBookings: 'No bookings yet',
      noBookingsDesc: 'Start by browsing available garages!',
      browseGarages: 'Browse Garages',
      status: 'Status',
      pending: 'Pending',
      confirmed: 'Confirmed',
      rejected: 'Rejected',
      completed: 'Completed',
      cancelled: 'Cancelled',
      bookingPeriod: 'Booking Period',
      totalPrice: 'Total Price',
      bookingType: 'Type',
      hourly: 'Hourly',
      daily: 'Daily',
      monthly: 'Monthly',
      message: 'Message',
      confirm: 'Confirm',
      reject: 'Reject',
      complete: 'Complete',
      cancel: 'Cancel',
      viewGarage: 'View Garage',
      cancellationNote: 'This booking has been cancelled.',
      bookedOn: 'Booked on',
      asRenter: 'As Renter',
      asOwner: 'As Owner',
      duration: 'Duration',
      days: 'Days',
      renter: 'Renter',
      owner: 'Owner',
      phone: 'Phone',
      cancellationReason: 'Cancellation Reason',
      cancelReasonPrompt: 'Please provide a reason for cancellation:',
      rejectReasonPrompt: 'Please provide a reason for rejection:',
      rejectSuccess: 'Booking rejected successfully',
      completeConfirm: 'Mark this booking as completed?',
      completeSuccess: 'Booking marked as completed!',
      tableNotFound: 'Bookings table not found. Please run FIX_BOOKINGS_TABLE.sql in your Supabase dashboard.',
   
    },
    
    // Notifications
    notifications: {
      title: 'Notifications',
      unread: 'unread',
      markAllRead: 'Mark all as read',
      filterAll: 'All',
      filterUnread: 'Unread',
      filterRead: 'Read',
      noNotifications: 'No notifications',
      noUnread: "You're all caught up! No unread notifications.",
      noRead: 'No read notifications yet.',
      defaultMessage: "You'll see notifications here when bookings are confirmed or rejected.",
      delete: 'Delete',
      markRead: 'Mark as read',
      justNow: 'Just now',
      minutesAgo: 'min ago',
      hoursAgo: 'hours ago',
      daysAgo: 'days ago',
      loading: 'Loading notifications...',
      
      // Notification Templates
      types: {
        booking_confirmed: {
          title: 'Booking Confirmed',
          message: 'Your booking for {garageName} has been confirmed!'
        },
        booking_rejected: {
          title: 'Booking Rejected',
          message: 'Your booking request for {garageName} was declined.'
        },
        new_booking: {
          title: 'New Booking Request',
          message: '{userName} wants to book {garageName}.'
        },
        booking_cancelled: {
          title: 'Booking Cancelled',
          message: 'Booking for {garageName} has been cancelled.'
        },
        payment_received: {
          title: 'Payment Received',
          message: 'You received ${amount} for {garageName}.'
        },
        review_received: {
          title: 'New Review',
          message: '{userName} left a review for {garageName}.'
        }
      }
    },
    
    // Theme Settings
    theme: {
      title: 'Choose Your Theme',
      subtitle: 'Customize your experience',
      default: 'Default',
      ocean: 'Ocean Blue',
      sunset: 'Sunset Orange',
      forest: 'Forest Green',
      midnight: 'Midnight Purple',
      rose: 'Rose Pink',
      dark: 'Dark Mode',
      reset: 'Reset to Default',
      close: 'Close',
      appearance: 'Appearance',
      appearanceSubtitle: 'Customize the look and feel of GarageShare',
      currentTheme: 'Current Theme',
      themeWillUpdate: 'Your theme preference will be saved automatically',
      chooseYourTheme: 'Choose Your Theme',
      themeChanged: 'Theme changed to',
      themeChangeFailed: 'Failed to change theme. Please try again.',
    
    },
    
    // Common
    common: {
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      close: 'Close',
      confirm: 'Confirm',
      loading: 'Loading...',
      error: 'Error',
      success: 'Success',
      search: 'Search',
      filter: 'Filter',
      reset: 'Reset',
      apply: 'Apply',
      back: 'Back',
      next: 'Next',
      submit: 'Submit',
      update: 'Update',
      required: 'Required',
      optional: 'Optional',
      yes: 'Yes',
      no: 'No',
      available: 'available',
    },
    
    // Map
    map: {
      viewMap: 'Map View',
      viewList: 'List View',
      yourLocation: 'Your Location',
      away: 'away',
      apiKeyMissing: 'Google Maps API key not configured',
      apiKeyHint: 'Please add REACT_APP_GOOGLE_MAPS_API_KEY to your .env file',
      locationError: 'Could not get your location. Please check your browser permissions.',
      selectLocation: 'Select Location on Map',
      pickerInstructions: 'Click on the map or drag the marker to select a location',
      useCurrentLocation: 'Use Current Location',
      gettingAddress: 'Getting address...',
      coordinates: 'Coordinates',
      showNearby: 'Show Nearby',
      distance: 'Distance',
      sortByDistance: 'Sort by Distance',
    },
    
    // Features
    features: {
      '24/7 Access': '24/7 Access',
      '24/7 Security': '24/7 Security',
      'Security Camera': 'Security Camera',
      'Security System': 'Security System',
      'CCTV': 'CCTV',
      'Gated': 'Gated',
      'Smart Lock': 'Smart Lock',
      'Well-lit': 'Well-lit',
      'Lighting': 'Lighting',
      'Electric Outlet': 'Electric Outlet',
      'Electric Charging': 'Electric Charging',
      'Climate Controlled': 'Climate Controlled',
      'Covered': 'Covered',
      'Covered Parking': 'Covered Parking',
      'Car Wash': 'Car Wash',
      'Double Door': 'Double Door',
      'Workbench': 'Workbench',
      'Storage Shelves': 'Storage Shelves',
      'Water Access': 'Water Access',
      'Wi-Fi Available': 'Wi-Fi Available',
      'Valet Parking': 'Valet Parking',
      'Maintenance Service': 'Maintenance Service',
      'Automated Door': 'Automated Door',
    },
    
    // Time
    time: {
      hour: 'hour',
      hours: 'hours',
      day: 'day',
      days: 'days',
      month: 'month',
      months: 'months',
      week: 'week',
      weeks: 'weeks',
    },
    
    // Price Units
    priceUnit: {
      hour: 'Per Hour',
      day: 'Per Day',
      week: 'Per Week',
      month: 'Per Month',
    },
    
    // Sizes
    sizes: {
      single: 'Single Car',
      double: 'Double Car',
      large: 'Large (5+ cars)',
      rv: 'RV/Truck Space',
      small: 'Small (Compact)',
      medium: 'Medium (Sedan)',
      extra_large: 'Extra Large',
    },
    
    // Capacity
    capacity: {
      single: '1 car',
      double: '2 cars',
      large: '5+ cars',
      rv: '1 RV/truck',
      small: '1 small car',
      medium: '1 sedan',
      'extra-large': 'Multiple vehicles',
      label: 'Capacity',
      available: 'Available',
      full: 'Full',
      spots: 'spots',
      spot: 'spot',
    },
    
    // Extended Common Terms
    commonExtended: {
      more: 'more',
      loadMore: 'Load More',
      new: 'New',
      booking: 'booking',
      bookings: 'bookings',
      garage: 'garage',
      garages: 'garages',
      selected: 'selected',
      available: 'available',
      unavailable: 'unavailable',
      total: 'Total',
      from: 'from',
      to: 'to',
      per: 'per',
      all: 'All',
      none: 'None',
      showing: 'Showing',
      of: 'of',
      results: 'results',
      result: 'result',
      views: 'Views',
      rating: 'Rating',
    },
    
    // Auth Messages
    auth: {
      signInRequired: 'Sign in to view owner contact information',
      loginToBook: 'Login to Book',
      signInToBook: 'Sign in to book this garage',
      ownGarage: 'This is your garage',
    },

    // Admin Dashboard
    admin: {
      title: 'Admin Dashboard',
      subtitle: 'Platform Analytics & Management',
      overview: 'Overview',
      statistics: 'Statistics',
      recentActivity: 'Recent Activity',
      topGarages: 'Top Garages',
      quickActions: 'Quick Actions',
      
      // Stats Cards
      totalGarages: 'Total Garages',
      availableGarages: 'Available Garages',
      totalUsers: 'Total Users',
      totalBookings: 'Total Bookings',
      confirmedBookings: 'Confirmed',
      pendingBookings: 'Pending',
      totalRevenue: 'Total Revenue',
      monthlyStats: 'Last 30 Days',
      confirmed: 'confirmed',
      pending: 'pending',
      cancelled: 'cancelled',
      completed: 'completed',
      last30Days: 'Last 30 Days',
      newBookings: 'New Bookings',
      newGarages: 'New Garages',
      newUsers: 'New Users',
      bookingCount: 'bookings',
      
      // Recent Bookings
      recentBookings: 'Recent Bookings',
      bookingId: 'Booking ID',
      renter: 'Renter',
      garage: 'Garage',
      dates: 'Dates',
      status: 'Status',
      price: 'Price',
      noRecentBookings: 'No recent bookings',
      
      // Top Garages
      topGaragesByBookings: 'Top Garages by Bookings',
      bookings: 'bookings',
      revenue: 'revenue',
      owner: 'Owner',
      noTopGarages: 'No top garages yet',
      
      // Quick Actions
      viewAllUsers: 'View All Users',
      manageGarages: 'Manage Garages',
      manageBookings: 'Manage Bookings',
      manageBugReports: 'Manage Bug Reports',
      manageDisputes: 'Manage Disputes',
      systemSettings: 'System Settings',
      reports: 'Generate Reports',
      refreshData: 'Refresh Data',
      
      // Bug Reports
      bugReports: 'Bug Reports',
      noBugReports: 'No bug reports',
      
      // Loading & Errors
      loading: 'Loading analytics...',
      error: 'Failed to load analytics data',
      retry: 'Retry',
    },

    // Disputes
    disputes: {
      title: 'Dispute Resolutions',
      subtitle: 'Manage and resolve booking disputes',
      noDisputes: 'No disputes to resolve',
      newDispute: 'New Dispute',
      viewDispute: 'View Details',
      resolveDispute: 'Resolve Dispute',
      
      // Dispute List
      filterStatus: 'Filter by Status',
      filterPriority: 'Filter by Priority',
      disputeId: 'Dispute ID',
      booking: 'Booking',
      initiator: 'Initiated By',
      respondent: 'Respondent',
      filed: 'Filed',
      dueBy: 'Due By',
      
      // Status
      statusOpen: 'Open',
      statusInReview: 'In Review',
      statusMediation: 'Mediation',
      statusResolved: 'Resolved',
      statusRejected: 'Rejected',
      
      // Priority
      priorityLow: 'Low',
      priorityMedium: 'Medium',
      priorityHigh: 'High',
      priorityCritical: 'Critical',
      
      // Dispute Types
      bookingIssue: 'Booking Issue',
      paymentIssue: 'Payment Issue',
      damageClaim: 'Damage Claim',
      communicationProblem: 'Communication Problem',
      conditionMismatch: 'Condition Mismatch',
      accessIssue: 'Access Issue',
      other: 'Other',
      
      // Details
      title: 'Dispute Title',
      description: 'Description',
      respondentResponse: 'Respondent Response',
      evidence: 'Evidence & Attachments',
      adminNotes: 'Admin Notes',
      
      // Actions
      markInReview: 'Mark as In Review',
      requestMediation: 'Request Mediation',
      approve: 'Approve',
      reject: 'Reject',
      offerCompensation: 'Offer Compensation',
      initiateRefund: 'Initiate Refund',
      close: 'Close Dispute',
      reassign: 'Reassign to Admin',
      
      // Resolution
      resolutionStatus: 'Resolution Status',
      resolutionApproved: 'Approved',
      resolutionRejected: 'Rejected',
      refundInitiated: 'Refund Initiated',
      compensationApproved: 'Compensation Approved',
      resolutionAmount: 'Resolution Amount',
      resolutionNotes: 'Resolution Notes',
      
      // Form
      addAdminNotes: 'Add admin notes...',
      selectResolution: 'Select resolution type',
      enterAmount: 'Enter amount',
      fileEvidence: 'Upload evidence',
      addNote: 'Add note',
      
      // Messages
      disputeCreated: 'Dispute created successfully',
      disputeResolved: 'Dispute resolved successfully',
      disputeAssigned: 'Dispute assigned to you',
      pendingResponse: 'Awaiting respondent response',
      responseReceived: 'Respondent has responded',
      
      // Timestamps
      createdAt: 'Created',
      resolvedAt: 'Resolved',
      daysOpen: 'days open',
    },

    // File Dispute
    fileDispute: {
      title: 'File a Dispute',
      subtitle: 'Describe your booking issue to get it resolved',
      disputeType: 'Dispute Type',
      priority: 'Priority Level',
      titleLabel: 'Dispute Title',
      titlePlaceholder: 'Brief title of the issue (5-100 characters)',
      titleRequired: 'Please enter a dispute title',
      titleTooShort: 'Title must be at least 5 characters',
      descriptionLabel: 'Detailed Description',
      descriptionPlaceholder: 'Explain what happened and why you are filing this dispute. Include dates, times, and relevant details (min 20 characters)',
      descriptionRequired: 'Please provide a detailed description',
      descriptionTooShort: 'Description must be at least 20 characters',
      info: 'Your dispute will be reviewed by our admin team. A fair resolution will be determined based on evidence and platform policies.',
      filDispute: 'File Dispute',
      filing: 'Filing...',
      disputeFiled: 'Dispute filed successfully. An admin will review it shortly.',
    },

    // Bug Report
    bugReport: {
      title: 'Report a Bug',
      subtitle: 'Help us improve by reporting technical issues',
      category: 'Category',
      categoryGeneral: 'General',
      categoryUI: 'User Interface',
      categoryBooking: 'Booking',
      categoryPayment: 'Payment',
      categorySearch: 'Search',
      categoryMap: 'Map',
      categoryNotification: 'Notifications',
      categoryAuthentication: 'Authentication',
      categoryPerformance: 'Performance',
      categoryMobile: 'Mobile',
      categoryOther: 'Other',
      severity: 'Severity Level',
      severityLow: 'Low (Minor inconvenience)',
      severityMedium: 'Medium (Feature not working)',
      severityHigh: 'High (Major functionality broken)',
      severityCritical: 'Critical (App unusable)',
      titleLabel: 'Bug Title',
      titlePlaceholder: 'Brief description of the bug (5-100 characters)',
      titleRequired: 'Please enter a bug title',
      titleTooShort: 'Title must be at least 5 characters',
      descriptionLabel: 'Detailed Description',
      descriptionPlaceholder: 'Describe what you did, what went wrong, and what you expected to happen. Include steps to reproduce if possible (min 20 characters)',
      descriptionRequired: 'Please provide a detailed description',
      descriptionTooShort: 'Description must be at least 20 characters',
      pageLabel: 'Page/Feature (Optional)',
      pagePlaceholder: 'e.g., /garage/123, booking form, search page',
      pageHint: 'Help us identify where the bug occurred',
      info: 'Your bug report helps us improve GarageShare. Our team will review it and work on a fix. Thank you for your feedback!',
      submitReport: 'Submit Report',
      submitting: 'Submitting...',
      reportedSuccessfully: 'Bug report submitted successfully. Thank you for helping us improve!',
    },

    // Bug Report Admin
    bugReportAdmin: {
      title: 'Bug Report Management',
      subtitle: 'Review and manage all bug reports from users',
      backToDashboard: 'Back to Dashboard',
      totalReports: 'Total Reports',
      statusOpen: 'Open',
      statusInProgress: 'In Progress',
      statusResolved: 'Resolved',
      statusClosed: 'Closed',
      statusWontFix: "Won't Fix",
      filterStatus: 'Status:',
      filterSeverity: 'Severity:',
      filterAll: 'All',
      allStatus: 'All Status',
      allSeverity: 'All Severity',
      refresh: 'Refresh',
      noReports: 'No bug reports found',
      loading: 'Loading bug reports...',
      category: 'Category',
      page: 'Page',
      browser: 'Browser',
      viewDetails: 'View Details',
      startWorking: 'Start Working',
      markResolved: 'Mark Resolved',
      close: 'Close',
      wontFix: "Won't Fix",
      delete: 'Delete',
      deleteConfirm: 'Are you sure you want to delete this bug report? This action cannot be undone.',
      statusUpdated: 'Bug report marked as',
      deleted: 'Bug report deleted successfully',
      loadFailed: 'Failed to load bug reports',
      updateFailed: 'Failed to update status',
      deleteFailed: 'Failed to delete bug report',
      description: 'Description',
      pageUrl: 'Page URL',
      browserInfo: 'Browser Information',
      userInfo: 'User Information',
      userName: 'Name',
      userEmail: 'Email',
      timeline: 'Timeline',
      created: 'Created',
      updated: 'Updated',
      adminNotes: 'Admin Notes',
      notesPlaceholder: 'Add internal notes about this bug report...',
      saveNote: 'Save Note',
      anonymous: 'Anonymous',
    },
  },
  
  sl: {
    // Header
    header: {
      myGarages: 'Moje Garaže',
      login: 'Prijava',
      signUp: 'Registracija',
      notifications: 'Obvestila',
      changeTheme: 'Spremeni Temo',
      theme: 'Tema',
      myProfile: 'Moj Profil',
      listYourGarage: 'Objavi Garažo',
      myBookings: 'Moje Rezervacije',
      reportBug: 'Prijavi Napako',
      logout: 'Odjava',
    },
    
    // Home/GarageList
    home: {
      title: 'Najdite Popoln Parkirni Prostor',
      subtitle: 'Odkrijte in rezervirajte varne garažne prostore v vaši bližini',
      searchPlaceholder: 'Išči po lokaciji...',
      filterAll: 'Vse',
      priceRange: 'Cenovni Razpon',
      features: 'Lastnosti',
      priceFrom: 'Od',
      priceTo: 'Do',
      applyFilters: 'Uporabi Filtre',
      resetFilters: 'Ponastavi Filtre',
      perHour: '/uro',
      perDay: '/dan',
      perMonth: '/mesec',
      available: 'Na voljo',
      viewDetails: 'Poglej Podrobnosti',
      noGarages: 'Ni najdenih garaž',
      noGaragesDesc: 'Poskusite prilagoditi filtre ali iskati na drugi lokaciji.',
      loading: 'Nalaganje garaž...',
    },
    
    // Garage Details
    garageDetail: {
      bookNow: 'Rezerviraj Zdaj',
      overview: 'Pregled',
      location: 'Lokacija',
      pricing: 'Cene',
      features: 'Lastnosti',
      availability: 'Razpoložljivost',
      hourly: 'Urno',
      daily: 'Dnevno',
      monthly: 'Mesečno',
      contactOwner: 'Kontaktiraj Lastnika',
      unavailable: 'Trenutno Nedostopno',
      selectDates: 'Izberite datume za rezervacijo te garaže',
      startDate: 'Začetni Datum',
      endDate: 'Končni Datum',
      bookingType: 'Tip Rezervacije',
      totalPrice: 'Skupna Cena',
      confirmBooking: 'Potrdi Rezervacijo',
      cancel: 'Prekliči',
      bookingSuccess: 'Zahteva za rezervacijo uspešno poslana!',
      bookingError: 'Neuspešna rezervacija',
    },
    
    // Login
    login: {
      title: 'Dobrodošli Nazaj',
      subtitle: 'Prijavite se v svoj račun',
      email: 'E-poštni Naslov',
      password: 'Geslo',
      forgotPassword: 'Pozabljeno Geslo?',
      loginButton: 'Prijava',
      noAccount: 'Nimate računa?',
      signUpLink: 'Registrirajte se',
      error: 'Neveljavne poverilnice',
    },
    
    // Register
    register: {
      title: 'Ustvari Račun',
      subtitle: 'Pridružite se GarageShare danes',
      name: 'Polno Ime',
      email: 'E-poštni Naslov',
      password: 'Geslo',
      confirmPassword: 'Potrdite Geslo',
      role: 'Želim',
      roleRenter: 'Najeti garažo',
      roleOwner: 'Objaviti garažo',
      signUpButton: 'Registracija',
      hasAccount: 'Že imate račun?',
      loginLink: 'Prijavite se',
      passwordMismatch: 'Gesli se ne ujemata',
      error: 'Registracija ni uspela',
    },
    
    // Profile
    profile: {
      title: 'Moj Profil',
      personalInfo: 'Osebni Podatki',
      accountSettings: 'Nastavitve Računa',
      name: 'Polno Ime',
      fullName: 'Polno Ime',
      email: 'E-poštni Naslov',
      phone: 'Telefonska Številka',
      role: 'Tip Računa',
      renter: 'Najemnik',
      owner: 'Lastnik',
      profilePicture: 'Profilna Slika',
      changePhoto: 'Spremeni Sliko',
      removePhoto: 'Odstrani Sliko',
      uploadPhoto: 'Naloži Sliko',
      remove: 'Odstrani',
      saveChanges: 'Shrani Spremembe',
      edit: 'Uredi Profil',
      cancel: 'Prekliči',
      updateSuccess: 'Profil uspešno posodobljen!',
      updateError: 'Neuspešna posodobitev profila',
      nameEmailRequired: 'Ime in e-pošta sta obvezna',
      invalidEmail: 'Vnesite veljaven e-poštni naslov',
      selectImageFile: 'Prosimo, izberite slikovno datoteko',
      imageMaxSize: 'Velikost slike mora biti manjša od 5MB',
      profilePictureUpdated: 'Profilna slika posodobljena!',
      profilePictureRemoved: 'Profilna slika odstranjena',
      accountInfo: 'Podatki o Računu',
      subtitle: 'Upravljajte podatke o svojem računu',
      notSet: 'Ni nastavljeno',
      memberSince: 'Član Od',
      userId: 'ID Uporabnika',
      saving: 'Shranjevanje...',
      // Password Section
      changePassword: 'Spremeni Geslo',
      currentPassword: 'Trenutno Geslo',
      newPassword: 'Novo Geslo',
      confirmPassword: 'Potrdite Novo Geslo',
      allPasswordFieldsRequired: 'Vsa polja za geslo so obvezna',
      passwordMinLength: 'Novo geslo mora imeti vsaj 6 znakov',
      passwordMismatch: 'Gesli se ne ujemata',
      passwordChangeSuccess: 'Geslo uspešno spremenjeno!',
      passwordChangeError: 'Neuspešna sprememba gesla',
      changing: 'Spreminjanje...',
      passwordSecurityTip: 'Zaščitite svoj račun z uporabo močnega gesla',
      passwordTip1: 'Uporabite vsaj 6 znakov',
      passwordTip2: 'Mešajte velike in male črke',
      passwordTip3: 'Vključite številke in posebne znake',
      passwordPlaceholder: 'Vsaj 6 znakov',
      // Statistics
      totalBookings: 'Skupaj Rezervacij',
      listedGarages: 'Objavljene Garaže',
      totalEarnings: 'Skupni Zaslužek',
      // Delete Account
      dangerZone: 'Nevarna Cona',
      deleteAccount: 'Izbriši Račun',
      deleteAccountWarning: 'Ko izbrišete svoj račun, ni poti nazaj. Prosimo, bodite prepričani.',
      deleteAccountConfirm: 'Ali ste prepričani, da želite izbrisati svoj račun? Tega dejanja ni mogoče razveljaviti.',
      deleteAccountSubtext: 'Vsi vaši podatki, vključno z rezervacijami in objavami, bodo trajno izbrisani.',
      enterPasswordConfirm: 'Vnesite svoje geslo za potrditev',
      deleting: 'Brisanje...',
      yesDeleteAccount: 'Da, Izbriši Moj Račun',
      accountDeleted: 'Račun uspešno izbrisan. Preusmerjanje...',
      deleteAccountError: 'Neuspešno brisanje računa',
      enterPasswordToConfirm: 'Prosimo, vnesite svoje geslo za potrditev',
      // Language Settings
      language: 'Jezik',
      languageSubtitle: 'Izberite svoj prednostni jezik',
    },
    
    // Add Garage
    addGarage: {
      title: 'Objavi Svojo Garažo',
      subtitle: 'Delite svoj garažni prostor in zaslužite',
      basicInfo: 'Osnovne Informacije',
      garageName: 'Ime Garaže',
      namePlaceholder: 'npr. Varno Parkiranje v Centru',
      description: 'Opis',
      descriptionPlaceholder: 'Opišite svoj garažni prostor, prednosti lokacije in posebne lastnosti...',
      location: 'Lokacija',
      address: 'Ulica',
      addressPlaceholder: 'Glavna ulica 123',
      city: 'Mesto',
      state: 'Regija',
      postalCode: 'Poštna Številka',
      country: 'Država',
      pricing: 'Cene in Velikost',
      capacity: 'Kapaciteta',
      hourlyRate: 'Urna Cena (€)',
      dailyRate: 'Dnevna Cena (€)',
      monthlyRate: 'Mesečna Cena (€)',
      features: 'Lastnosti',
      selectFeatures: 'Izberite vse veljavne',
      covered: 'Pokrito',
      security: '24/7 Varnost',
      cctv: 'Video Nadzor',
      electricCharging: 'Električno Polnjenje',
      carWash: 'Avtopralnica',
      lighting: 'Razsvetljava',
      images: 'Slike',
      uploadImages: 'Naložite slike svoje garaže (obvezno, največ 5MB vsaka)',
      clickUpload: 'Kliknite za nalaganje slik',
      dragDrop: 'PNG, JPG, GIF do 5MB',
      availability: 'Razpoložljivost',
      availableNow: 'Takoj Na Voljo',
      cancelButton: 'Prekliči',
      submitButton: 'Objavi Garažo',
      updateButton: 'Posodobi Garažo',
      createSuccess: 'Garaža uspešno objavljena!',
      updateSuccess: 'Garaža uspešno posodobljena!',
      createError: 'Neuspešna objava garaže. Prosimo, poskusite znova.',
      fillRequired: 'Prosimo, izpolnite vsa obvezna polja',
      priceGreaterThanZero: 'Cena mora biti večja od 0',
      uploadOneImage: 'Prosimo, naložite vsaj eno sliko',
      uploadingImages: 'Nalaganje slik...',
      savingDetails: 'Shranjevanje podatkov...',
      uploadImageError: 'Neuspešno nalaganje slike',
      listing: 'Objavljanje garaže...',
    },
    
    // Edit Garage
    editGarage: {
      title: 'Uredi Svojo Garažo',
      subtitle: 'Posodobi informacije o svoji garaži',
      loadingData: 'Nalaganje podatkov o garaži...',
      notFound: 'Garaža ni najdena',
      noPermission: 'Nimate dovoljenja za urejanje te garaže',
      failedLoad: 'Neuspešno nalaganje podatkov o garaži',
      updateSuccess: 'Garaža uspešno posodobljena!',
      updateError: 'Neuspešna posodobitev garaže. Prosimo, poskusite znova.',
      uploadingImages: 'Nalaganje novih slik...',
      updatingDetails: 'Posodabljanje podatkov o garaži...',
      updating: 'Posodabljanje Garaže...',
      imageError: 'Prosimo, izberite samo slikovne datoteke',
      imageSizeError: 'Slike morajo biti manjše od 5MB',
    },
    
    // My Garages
    myGarages: {
      title: 'Moje Garaže',
      subtitle: 'Upravljajte svoje objave garaž',
      addNew: 'Dodaj Novo Garažo',
      noGarages: 'Še Nimate Objavljenih Garaž',
      noGaragesDesc: 'Začnite zaslužiti z objavo svoje prve garaže!',
      listFirst: 'Objavite Svojo Prvo Garažo',
      edit: 'Uredi',
      delete: 'Izbriši',
      deleteConfirm: 'Izbriši Garažo',
      deleteWarning: 'Ali ste prepričani, da želite izbrisati',
      deleteSubtext: 'Tega dejanja ni mogoče razveljaviti. Vsa zgodovina rezervacij in podatki te garaže bodo trajno odstranjeni.',
      deleteSuccess: 'Garaža uspešno izbrisana!',
      deleteError: 'Neuspešno brisanje garaže',
      availabilitySuccess: 'Razpoložljivost uspešno posodobljena!',
      availabilityError: 'Neuspešna posodobitev razpoložljivosti',
      loadingGarages: 'Nalaganje vaših garaž...',
      available: 'Na voljo',
      unavailable: 'Ni na voljo',
      active: 'Aktivno',
      inactive: 'Neaktivno',
      noImage: 'Ni Slike',
      price: 'Cena',
      size: 'Velikost',
      features: 'Lastnosti',
      moreFeatures: 'več',
      views: 'Ogledi',
      bookings: 'Rezervacije',
      rating: 'Ocena',
      cancel: 'Prekliči',
      confirmDelete: 'Da, Izbriši Garažo',
    },
    
    // Notifications
    notifications: {
      title: 'Obvestila',
      unread: 'neprebrano',
      markAllRead: 'Označi vse kot prebrano',
      filterAll: 'Vse',
      filterUnread: 'Neprebrano',
      filterRead: 'Prebrano',
      noNotifications: 'Ni obvestil',
      noUnread: 'Vse ste pregledali! Ni neprebranjih obvestil.',
      noRead: 'Še ni prebranih obvestil.',
      defaultMessage: 'Obvestila boste videli tukaj, ko bodo rezervacije potrjene ali zavrnjene.',
      delete: 'Izbriši',
      markRead: 'Označi kot prebrano',
      justNow: 'Pravkar',
      minutesAgo: 'min nazaj',
      hoursAgo: 'ur nazaj',
      daysAgo: 'dni nazaj',
      loading: 'Nalaganje obvestil...',
      
      // Notification Templates
      types: {
        booking_confirmed: {
          title: 'Rezervacija Potrjena',
          message: 'Vaša rezervacija za {garageName} je bila potrjena!'
        },
        booking_rejected: {
          title: 'Rezervacija Zavrnjena',
          message: 'Vaša zahteva za rezervacijo {garageName} je bila zavrnjena.'
        },
        new_booking: {
          title: 'Nova Zahteva za Rezervacijo',
          message: '{userName} želi rezervirati {garageName}.'
        },
        booking_cancelled: {
          title: 'Rezervacija Preklicana',
          message: 'Rezervacija za {garageName} je bila preklicana.'
        },
        payment_received: {
          title: 'Prejeto Plačilo',
          message: 'Prejeli ste ${amount} za {garageName}.'
        },
        review_received: {
          title: 'Nova Ocena',
          message: '{userName} je pustil oceno za {garageName}.'
        }
      }
    },
    
    // Theme Settings
    theme: {
      title: 'Izberite Svojo Temo',
      subtitle: 'Prilagodite svojo izkušnjo',
      default: 'Privzeta',
      ocean: 'Ocean Modra',
      sunset: 'Sončni Zahod',
      forest: 'Gozdna Zelena',
      midnight: 'Polnočna Vijolična',
      rose: 'Rožnata',
      dark: 'Temni Način',
      reset: 'Ponastavi na Privzeto',
      close: 'Zapri',
      appearance: 'Videz',
      appearanceSubtitle: 'Prilagodite izgled in občutek aplikacije GarageShare',
      currentTheme: 'Trenutna Tema',
      themeWillUpdate: 'Vaša izbira teme bo samodejno shranjena',
      chooseYourTheme: 'Izberite Svojo Temo',
      themeChanged: 'Tema spremenjena na',
      themeChangeFailed: 'Neuspešna sprememba teme. Prosimo, poskusite znova.',
   
    },
    
    // Common
    common: {
      save: 'Shrani',
      cancel: 'Prekliči',
      delete: 'Izbriši',
      edit: 'Uredi',
      close: 'Zapri',
      confirm: 'Potrdi',
      loading: 'Nalaganje...',
      error: 'Napaka',
      success: 'Uspeh',
      search: 'Iskanje',
      filter: 'Filter',
      reset: 'Ponastavi',
      apply: 'Uporabi',
      back: 'Nazaj',
      next: 'Naprej',
      submit: 'Pošlji',
      update: 'Posodobi',
      required: 'Obvezno',
      optional: 'Neobvezno',
      yes: 'Da',
      no: 'Ne',
      available: 'na voljo',
    },
    
    // Map
    map: {
      viewMap: 'Pogled Zemljevida',
      viewList: 'Pogled Seznama',
      yourLocation: 'Vaša Lokacija',
      away: 'stran',
      apiKeyMissing: 'Google Maps API ključ ni nastavljen',
      apiKeyHint: 'Prosimo, dodajte REACT_APP_GOOGLE_MAPS_API_KEY v vašo .env datoteko',
      locationError: 'Ni bilo mogoče pridobiti vaše lokacije. Preverite dovoljenja brskalnika.',
      selectLocation: 'Izberi Lokacijo na Zemljevidu',
      pickerInstructions: 'Kliknite na zemljevid ali povlecite označbo za izbiro lokacije',
      useCurrentLocation: 'Uporabi Trenutno Lokacijo',
      gettingAddress: 'Pridobivanje naslova...',
      coordinates: 'Koordinate',
      showNearby: 'Prikaži Bližnje',
      distance: 'Razdalja',
      sortByDistance: 'Razvrsti po Razdalji',
    },
    
    // Features
    features: {
      '24/7 Access': '24/7 Dostop',
      '24/7 Security': '24/7 Varovanje',
      'Security Camera': 'Varnostna Kamera',
      'Security System': 'Varnostni Sistem',
      'CCTV': 'Video Nadzor',
      'Gated': 'Ograjen Prostor',
      'Smart Lock': 'Pametna Ključavnica',
      'Well-lit': 'Dobro Osvetljen',
      'Lighting': 'Osvetlitev',
      'Electric Outlet': 'Električna Vtičnica',
      'Electric Charging': 'Polnilna Postaja',
      'Climate Controlled': 'Klimatizirano',
      'Covered': 'Pokrito',
      'Covered Parking': 'Pokrito Parkiranje',
      'Car Wash': 'Avtopralnica',
      'Double Door': 'Dvojna Vrata',
      'Workbench': 'Delovna Miza',
      'Storage Shelves': 'Shrambene Police',
      'Water Access': 'Dostop do Vode',
      'Wi-Fi Available': 'Wi-Fi Na Voljo',
      'Valet Parking': 'Valet Storitev',
      'Maintenance Service': 'Vzdrževanje',
      'Automated Door': 'Avtomatska Vrata',
    },
    
    // Time
    time: {
      hour: 'ura',
      hours: 'ur',
      day: 'dan',
      days: 'dni',
      month: 'mesec',
      months: 'mesecev',
      week: 'teden',
      weeks: 'tedni',
    },
    
    // Price Units
    priceUnit: {
      hour: 'Na Uro',
      day: 'Na Dan',
      week: 'Na Teden',
      month: 'Na Mesec',
    },
    
    // Sizes
    sizes: {
      single: 'En Avto',
      double: 'Dva Avta',
      large: 'Velika (5+ avtov)',
      rv: 'RV/Tovornjak',
      small: 'Majhna (Kompakt)',
      medium: 'Srednja (Sedan)',
      extra_large: 'Zelo Velika',
    },
    
    // Capacity
    capacity: {
      single: '1 avto',
      double: '2 avta',
      large: '5+ avtov',
      rv: '1 RV/tovornjak',
      small: '1 majhen avto',
      medium: '1 sedan',
      'extra-large': 'Več vozil',
      label: 'Kapaciteta',
      available: 'Na voljo',
      full: 'Polno',
      spots: 'mest',
      spot: 'mesto',
    },
    
    // Extended Common Terms
    commonExtended: {
      more: 'več',
      loadMore: 'Naloži več',
      new: 'Novo',
      booking: 'rezervacija',
      bookings: 'rezervacije',
      garage: 'garaža',
      garages: 'garaže',
      selected: 'izbrano',
      available: 'na voljo',
      unavailable: 'ni na voljo',
      total: 'Skupaj',
      from: 'od',
      to: 'do',
      per: 'na',
      all: 'Vse',
      none: 'Nič',
      showing: 'Prikazujem',
      of: 'od',
      results: 'rezultatov',
      result: 'rezultat',
      views: 'Ogledi',
      rating: 'Ocena',
    },
    
    // Auth Messages
    auth: {
      signInRequired: 'Prijavite se za ogled kontaktnih podatkov lastnika',
      loginToBook: 'Prijavite se za Rezervacijo',
      signInToBook: 'Prijavite se za rezervacijo te garaže',
      ownGarage: 'To je vaša garaža',
    },
 
    myBookings: {
      title: 'Moje Rezervacije',
      viewAll: 'Vse',
      viewPending: 'Čakajoče',
      viewConfirmed: 'Potrjene',
      viewCompleted: 'Zaključene',
      viewCancelled: 'Preklicane',
      filterAll: 'Vse',
      filterUpcoming: 'Prihajajoče',
      filterPast: 'Pretekle',
      noBookings: 'Še nimate rezervacij',
      noBookingsDesc: 'Začnite z brskanjem po razpoložljivih garažah!',
      noBookingsRenter: 'Še niste naredili nobene rezervacije. Začnite z brskanjem po razpoložljivih garažah!',
      noBookingsOwner: 'Še niste prejeli nobenih zahtev za rezervacijo.',
      browseGarages: 'Prebrskaj Garaže',
      status: 'Status',
      pending: 'Čakajoče',
      confirmed: 'Potrjeno',
      rejected: 'Zavrnjeno',
      completed: 'Zaključeno',
      cancelled: 'Preklicano',
      bookingPeriod: 'Obdobje Rezervacije',
      totalPrice: 'Skupna Cena',
      bookingType: 'Tip',
      hourly: 'Urno',
      daily: 'Dnevno',
      monthly: 'Mesečno',
      message: 'Sporočilo',
      confirm: 'Potrdi',
      reject: 'Zavrni',
      complete: 'Zaključi',
      cancel: 'Prekliči',
      viewGarage: 'Poglej Garažo',
      cancellationNote: 'Ta rezervacija je bila preklicana.',
      bookedOn: 'Rezervirano',
      
      // NEW KEYS
      asRenter: 'Kot Najemnik',
      asOwner: 'Kot Lastnik',
      duration: 'Trajanje',
      days: 'Dni',
      renter: 'Najemnik',
      owner: 'Lastnik',
      phone: 'Telefon',
      cancellationReason: 'Razlog za Preklic',
      cancelReasonPrompt: 'Prosimo, navedite razlog za preklic:',
      rejectReasonPrompt: 'Prosimo, navedite razlog za zavrnitev:',
      rejectSuccess: 'Rezervacija uspešno zavrnjena',
      completeConfirm: 'Želite označiti to rezervacijo kot zaključeno?',
      completeSuccess: 'Rezervacija označena kot zaključena!',
      tableNotFound: 'Tabela rezervacij ni najdena. Prosimo, zaženite FIX_BOOKINGS_TABLE.sql v vašem Supabase nadzorni plošči.',
    },
    
    // Admin Dashboard
    admin: {
      title: 'Nadzorna Plošča za Administratorje',
      subtitle: 'Analitika in Upravljanje Platforme',
      overview: 'Pregled',
      statistics: 'Statistike',
      recentActivity: 'Nedavna Aktivnost',
      topGarages: 'Najboljše Garaže',
      quickActions: 'Hitre Akcije',
      
      // Stats Cards
      totalGarages: 'Skupaj Garaž',
      availableGarages: 'Razpoložljive Garaže',
      totalUsers: 'Skupaj Uporabnikov',
      totalBookings: 'Skupaj Rezervacij',
      confirmedBookings: 'Potrjene',
      pendingBookings: 'Čakajoče',
      totalRevenue: 'Skupni Prihodek',
      monthlyStats: 'Zadnjih 30 Dni',
      confirmed: 'potrjeno',
      pending: 'čakajoče',
      cancelled: 'preklicano',
      completed: 'zaključeno',
      last30Days: 'Zadnjih 30 Dni',
      newBookings: 'Nove Rezervacije',
      newGarages: 'Nove Garaže',
      newUsers: 'Novi Uporabniki',
      bookingCount: 'rezervacije',
      
      // Recent Bookings
      recentBookings: 'Nedavne Rezervacije',
      bookingId: 'ID Rezervacije',
      renter: 'Najemnik',
      garage: 'Garaža',
      dates: 'Datumi',
      status: 'Status',
      price: 'Cena',
      noRecentBookings: 'Ni nedavnih rezervacij',
      
      // Top Garages
      topGaragesByBookings: 'Najboljše Garaže po Rezervacijah',
      rank: 'Rang',
      garageName: 'Ime Garaže',
      location: 'Lokacija',
      bookingCount: 'Rezervacije',
      revenue: 'Prihodek',
      owner: 'Lastnik',
      noTopGarages: 'Podatki o rezervacijah še niso na voljo',
      
      // Quick Actions
      manageGarages: 'Upravljaj Garaže',
      manageBookings: 'Upravljaj Rezervacije',
      manageBugReports: 'Upravljaj Poročila o Napakah',
      manageDisputes: 'Upravljaj Spore',
      viewReports: 'Poglej Poročila',
      systemSettings: 'Sistemske Nastavitve',
      refreshData: 'Osveži Podatke',
      
      // Bug Reports
      bugReports: 'Poročila o Napakah',
      noBugReports: 'Ni poročil o napakah',
      
      // Status
      pending: 'Čakajoče',
      confirmed: 'Potrjeno',
      completed: 'Zaključeno',
      cancelled: 'Preklicano',
      rejected: 'Zavrnjeno',
      
      // Loading & Errors
      loading: 'Nalaganje analitike...',
      loadingError: 'Neuspešno nalaganje podatkov o analitiki',
      accessDenied: 'Dostop Zavrnjen',
      adminOnly: 'Ta stran je dostopna samo administratorjem',
      redirecting: 'Preusmerjanje na domačo stran...',
      
      // Metrics
      newBookings: 'Nove Rezervacije',
      newUsers: 'Novi Uporabniki',
      newGarages: 'Nove Garaže',
      last30Days: 'Zadnjih 30 Dni',
      growth: 'Rast',
      
      // Time
      to: 'do',
      at: 'ob',
    },

    // Disputes
    disputes: {
      title: 'Razreševanje Sporov',
      subtitle: 'Upravljajte in razreševajte spore o rezervacijah',
      noDisputes: 'Ni sporov za razreševanje',
      newDispute: 'Nov Spor',
      viewDispute: 'Poglej Podrobnosti',
      resolveDispute: 'Razreši Spor',
      
      // Dispute List
      filterStatus: 'Filtriraj po Statusu',
      filterPriority: 'Filtriraj po Prioriteti',
      disputeId: 'ID Spora',
      booking: 'Rezervacija',
      initiator: 'Sprožil',
      respondent: 'Odgovori',
      filed: 'Vloženo',
      dueBy: 'Dospelo',
      
      // Status
      statusOpen: 'Odprto',
      statusInReview: 'V Pregledu',
      statusMediation: 'Medijacija',
      statusResolved: 'Razrešeno',
      statusRejected: 'Zavrnjeno',
      
      // Priority
      priorityLow: 'Nizka',
      priorityMedium: 'Srednja',
      priorityHigh: 'Visoka',
      priorityCritical: 'Kritična',
      
      // Dispute Types
      bookingIssue: 'Težava z Rezervacijo',
      paymentIssue: 'Težava s Plačilom',
      damageClaim: 'Zahtevek za Škodo',
      communicationProblem: 'Problem s Komunikacijo',
      conditionMismatch: 'Neskladje Stanja',
      accessIssue: 'Problem s Dostopom',
      other: 'Drugo',
      
      // Details
      title: 'Naslov Spora',
      description: 'Opis',
      respondentResponse: 'Odziv Odgovora',
      evidence: 'Dokazi in Priloge',
      adminNotes: 'Opombe Administratorja',
      
      // Actions
      markInReview: 'Označi kot V Pregledu',
      requestMediation: 'Zahtevaj Medijacijo',
      approve: 'Odobri',
      reject: 'Zavrni',
      offerCompensation: 'Ponudi Nadomestilo',
      initiateRefund: 'Zaženi Povračilo',
      close: 'Zapri Spor',
      reassign: 'Ponovno Dodeli Administratorju',
      
      // Resolution
      resolutionStatus: 'Status Razreške',
      resolutionApproved: 'Odobreno',
      resolutionRejected: 'Zavrnjeno',
      refundInitiated: 'Povračilo Zažeto',
      compensationApproved: 'Nadomestilo Odobreno',
      resolutionAmount: 'Znesek Razreške',
      resolutionNotes: 'Opombe Razreške',
      
      // Form
      addAdminNotes: 'Dodaj opombe administratorja...',
      selectResolution: 'Izberi vrsto razreške',
      enterAmount: 'Vnesite znesek',
      fileEvidence: 'Naložite dokaze',
      addNote: 'Dodaj opombo',
      
      // Messages
      disputeCreated: 'Spor je bil uspešno ustvarjen',
      disputeResolved: 'Spor je bil uspešno razrešen',
      disputeAssigned: 'Spor vam je bil dodeljen',
      pendingResponse: 'Čakanje na odziv odgovora',
      responseReceived: 'Odgovorni je odgovoril',
      
      // Timestamps
      createdAt: 'Ustvarjeno',
      resolvedAt: 'Razrešeno',
      daysOpen: 'dni odprto',
    },

    // File Dispute
    fileDispute: {
      title: 'Vložite Spor',
      subtitle: 'Opišite težavo z rezervacijo, da jo lahko razrešimo',
      disputeType: 'Vrsta Spora',
      priority: 'Raven Prioritete',
      titleLabel: 'Naslov Spora',
      titlePlaceholder: 'Kratek naslov težave (5-100 znakov)',
      titleRequired: 'Prosimo vnesite naslov spora',
      titleTooShort: 'Naslov mora biti dolg vsaj 5 znakov',
      descriptionLabel: 'Podroben Opis',
      descriptionPlaceholder: 'Razložite kaj se je zgodilo in zakaj vlagately spor. Vključite datume, ure in ustrezne podrobnosti (min 20 znakov)',
      descriptionRequired: 'Prosimo podajte podroben opis',
      descriptionTooShort: 'Opis mora biti dolg vsaj 20 znakov',
      info: 'Vaš spor bo pregledal naš admin tim. Poštena razreška bo določena na podlagi dokazov in politike platforme.',
      filDispute: 'Vloži Spor',
      filing: 'Oddajam...',
      disputeFiled: 'Spor je bil uspešno vložen. Admin ga bo kmalu pregledal.',
    },

    // Bug Report
    bugReport: {
      title: 'Prijavite Napako',
      subtitle: 'Pomagajte nam izboljšati z prijavo tehničnih težav',
      category: 'Kategorija',
      categoryGeneral: 'Splošno',
      categoryUI: 'Uporabniški Vmesnik',
      categoryBooking: 'Rezervacija',
      categoryPayment: 'Plačilo',
      categorySearch: 'Iskanje',
      categoryMap: 'Zemljevid',
      categoryNotification: 'Obvestila',
      categoryAuthentication: 'Prijava',
      categoryPerformance: 'Zmogljivost',
      categoryMobile: 'Mobilni Uređaj',
      categoryOther: 'Drugo',
      severity: 'Raven Resnosti',
      severityLow: 'Nizka (Manjša Neprijetnost)',
      severityMedium: 'Srednja (Funkcionalnost Ne Deluje)',
      severityHigh: 'Visoka (Glavna Funkcionalnost Pokvarjena)',
      severityCritical: 'Kritična (Aplikacija Neuporabna)',
      titleLabel: 'Naslov Napake',
      titlePlaceholder: 'Kratek Opis Napake (5-100 Znakov)',
      titleRequired: 'Prosimo Vnesite Naslov Napake',
      titleTooShort: 'Naslov Mora Biti Dolg Vsaj 5 Znakov',
      descriptionLabel: 'Podroben Opis',
      descriptionPlaceholder: 'Opišite Kaj Ste Naredili, Kaj Se Je Zgodilo Narobe, in Kaj Ste Pričakovali. Če Mogoče Dodajte Korake za Reprodukcijo (Min 20 Znakov)',
      descriptionRequired: 'Prosimo Podajte Podroben Opis',
      descriptionTooShort: 'Opis Mora Biti Dolg Vsaj 20 Znakov',
      pageLabel: 'Stran/Značilnost (Neobvezno)',
      pagePlaceholder: 'Npr. /garage/123, obrazec za rezervacijo, stran iskanja',
      pageHint: 'Pomagajte Nam Identificirati, Kje Se je Napaka Pojavila',
      info: 'Vaša Prijava Napake nam Pomaga Izboljšati GarageShare. Naš Tim Bo Pregledal in Delal na Popravku. Hvala za Vašo Povratno Informacijo!',
      submitReport: 'Oddaj Prijavo',
      submitting: 'Oddajam...',
      reportedSuccessfully: 'Prijava Napake Uspešno Oddana. Hvala, da Pomagate pri Izboljšavi!',
    },

    // Bug Report Admin
    bugReportAdmin: {
      title: 'Upravljanje Poročil o Napakah',
      subtitle: 'Preglejte in upravljajte vsa poročila o napakah uporabnikov',
      backToDashboard: 'Nazaj na Nadzorno Ploščo',
      totalReports: 'Vsa Poročila',
      statusOpen: 'Odprto',
      statusInProgress: 'V Delu',
      statusResolved: 'Razrešeno',
      statusClosed: 'Zaprto',
      statusWontFix: 'Ne Bo Popravljeno',
      filterStatus: 'Status:',
      filterSeverity: 'Resnost:',
      filterAll: 'Vse',
      allStatus: 'Vsi Statusi',
      allSeverity: 'Vse Resnosti',
      refresh: 'Osveži',
      noReports: 'Ni najdenih poročil o napakah',
      loading: 'Nalaganje poročil o napakah...',
      category: 'Kategorija',
      page: 'Stran',
      browser: 'Brskalnik',
      viewDetails: 'Poglej Podrobnosti',
      startWorking: 'Začni Delo',
      markResolved: 'Označi kot Razrešeno',
      close: 'Zapri',
      wontFix: 'Ne Bo Popravljeno',
      delete: 'Izbriši',
      deleteConfirm: 'Ali ste prepričani, da želite izbrisati to poročilo o napaki? Tega dejanja ni mogoče razveljaviti.',
      statusUpdated: 'Poročilo o napaki označeno kot',
      deleted: 'Poročilo o napaki uspešno izbrisano',
      loadFailed: 'Napaka pri nalaganju poročil',
      updateFailed: 'Napaka pri posodobitvi statusa',
      deleteFailed: 'Napaka pri brisanju poročila',
      description: 'Opis',
      pageUrl: 'URL Strani',
      browserInfo: 'Informacije o Brskalniku',
      userInfo: 'Informacije o Uporabniku',
      userName: 'Ime',
      userEmail: 'E-pošta',
      timeline: 'Časovnica',
      created: 'Ustvarjeno',
      updated: 'Posodobljeno',
      adminNotes: 'Opombe Administratorja',
      notesPlaceholder: 'Dodajte interne opombe o tem poročilu...',
      saveNote: 'Shrani Opombo',
      anonymous: 'Anonimen',
    },
  },

  de: {
    // Header
    header: {
      myGarages: 'Meine Garagen',
      notifications: 'Benachrichtigungen',
      myProfile: 'Mein Profil',
      listYourGarage: 'Garage inserieren',
      myBookings: 'Meine Buchungen',
      reportBug: 'Fehler melden',
      logout: 'Abmelden',
      login: 'Anmelden',
      signUp: 'Registrieren',
      changeTheme: 'Design ändern',
      theme: 'Design',
      changeLanguage: 'Sprache ändern'
    },

    // Home/GarageList
    home: {
      title: 'Finden Sie Ihren perfekten Parkplatz',
      subtitle: 'Entdecken und buchen Sie sichere Garagenplätze in Ihrer Nähe',
      searchPlaceholder: 'Nach Standort suchen...',
      filterAll: 'Alle',
      priceRange: 'Preisspanne',
      features: 'Ausstattung',
      priceFrom: 'Von',
      priceTo: 'Bis',
      applyFilters: 'Filter anwenden',
      resetFilters: 'Filter zurücksetzen',
      perHour: '/Stunde',
      perDay: '/Tag',
      perMonth: '/Monat',
      available: 'Verfügbar',
      viewDetails: 'Details ansehen',
      noGarages: 'Keine Garagen gefunden',
      noGaragesDesc: 'Passen Sie Ihre Filter an oder suchen Sie an einem anderen Ort.',
      loading: 'Garagen werden geladen...',
    },

    // Garage Details
    garageDetail: {
      bookNow: 'Jetzt buchen',
      overview: 'Übersicht',
      location: 'Standort',
      pricing: 'Preise',
      features: 'Ausstattung',
      availability: 'Verfügbarkeit',
      hourly: 'Stündlich',
      daily: 'Täglich',
      monthly: 'Monatlich',
      contactOwner: 'Eigentümer kontaktieren',
      unavailable: 'Derzeit nicht verfügbar',
      selectDates: 'Wählen Sie Ihre Daten, um diese Garage zu buchen',
      startDate: 'Startdatum',
      endDate: 'Enddatum',
      bookingType: 'Buchungsart',
      totalPrice: 'Gesamtpreis',
      confirmBooking: 'Buchung bestätigen',
      cancel: 'Abbrechen',
      bookingSuccess: 'Buchungsanfrage erfolgreich gesendet!',
      bookingError: 'Buchung fehlgeschlagen',
    },

    // Login
    login: {
      title: 'Willkommen zurück',
      subtitle: 'Bei Ihrem Konto anmelden',
      email: 'E-Mail-Adresse',
      password: 'Passwort',
      forgotPassword: 'Passwort vergessen?',
      loginButton: 'Anmelden',
      noAccount: 'Noch kein Konto?',
      signUpLink: 'Registrieren',
      error: 'Ungültige Anmeldedaten',
    },

    // Register
    register: {
      title: 'Konto erstellen',
      subtitle: 'Werden Sie noch heute Mitglied bei GarageShare',
      name: 'Vollständiger Name',
      email: 'E-Mail-Adresse',
      password: 'Passwort',
      confirmPassword: 'Passwort bestätigen',
      role: 'Ich möchte',
      roleRenter: 'Eine Garage mieten',
      roleOwner: 'Meine Garage inserieren',
      signUpButton: 'Registrieren',
      hasAccount: 'Haben Sie bereits ein Konto?',
      loginLink: 'Anmelden',
      passwordMismatch: 'Passwörter stimmen nicht überein',
      error: 'Registrierung fehlgeschlagen',
    },

    // Profile
    profile: {
      title: 'Mein Profil',
      personalInfo: 'Persönliche Informationen',
      accountSettings: 'Kontoeinstellungen',
      name: 'Vollständiger Name',
      fullName: 'Vollständiger Name',
      email: 'E-Mail-Adresse',
      phone: 'Telefonnummer',
      role: 'Kontotyp',
      renter: 'Mieter',
      owner: 'Eigentümer',
      profilePicture: 'Profilbild',
      changePhoto: 'Foto ändern',
      removePhoto: 'Foto entfernen',
      uploadPhoto: 'Foto hochladen',
      remove: 'Entfernen',
      saveChanges: 'Änderungen speichern',
      edit: 'Profil bearbeiten',
      cancel: 'Abbrechen',
      updateSuccess: 'Profil erfolgreich aktualisiert!',
      updateError: 'Profilaktualisierung fehlgeschlagen',
      nameEmailRequired: 'Name und E-Mail sind erforderlich',
      invalidEmail: 'Bitte eine gültige E-Mail-Adresse eingeben',
      selectImageFile: 'Bitte eine Bilddatei auswählen',
      imageMaxSize: 'Bildgröße muss kleiner als 5MB sein',
      profilePictureUpdated: 'Profilbild aktualisiert!',
      profilePictureRemoved: 'Profilbild entfernt',
      accountInfo: 'Kontoinformationen',
      subtitle: 'Verwalten Sie Ihre Kontoinformationen',
      notSet: 'Nicht festgelegt',
      memberSince: 'Mitglied seit',
      userId: 'Benutzer-ID',
      saving: 'Wird gespeichert...',
      // Password Section
      changePassword: 'Passwort ändern',
      currentPassword: 'Aktuelles Passwort',
      newPassword: 'Neues Passwort',
      confirmPassword: 'Neues Passwort bestätigen',
      allPasswordFieldsRequired: 'Alle Passwortfelder sind erforderlich',
      passwordMinLength: 'Neues Passwort muss mindestens 6 Zeichen enthalten',
      passwordMismatch: 'Passwörter stimmen nicht überein',
      passwordChangeSuccess: 'Passwort erfolgreich geändert!',
      passwordChangeError: 'Passwortänderung fehlgeschlagen',
      changing: 'Wird geändert...',
      passwordSecurityTip: 'Schützen Sie Ihr Konto mit einem starken Passwort',
      passwordTip1: 'Mindestens 6 Zeichen verwenden',
      passwordTip2: 'Groß- und Kleinbuchstaben mischen',
      passwordTip3: 'Zahlen und Sonderzeichen einfügen',
      passwordPlaceholder: 'Mindestens 6 Zeichen',
      // Statistics
      totalBookings: 'Gesamtbuchungen',
      listedGarages: 'Inserierte Garagen',
      totalEarnings: 'Gesamteinnahmen',
      // Delete Account
      dangerZone: 'Gefahrenzone',
      deleteAccount: 'Konto löschen',
      deleteAccountWarning: 'Sobald Sie Ihr Konto löschen, gibt es kein Zurück mehr. Bitte seien Sie sicher.',
      deleteAccountConfirm: 'Sind Sie sicher, dass Sie Ihr Konto löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
      deleteAccountSubtext: 'Alle Ihre Daten, einschließlich Buchungen und Inserate, werden dauerhaft gelöscht.',
      enterPasswordConfirm: 'Passwort zur Bestätigung eingeben',
      deleting: 'Wird gelöscht...',
      yesDeleteAccount: 'Ja, mein Konto löschen',
      accountDeleted: 'Konto erfolgreich gelöscht. Weiterleitung...',
      deleteAccountError: 'Kontolöschung fehlgeschlagen',
      enterPasswordToConfirm: 'Bitte geben Sie Ihr Passwort zur Bestätigung ein',
      // Language Settings
      language: 'Sprache',
      languageSubtitle: 'Bevorzugte Sprache auswählen',
    },

    // Add Garage
    addGarage: {
      title: 'Garage inserieren',
      subtitle: 'Teilen Sie Ihren Garagenplatz und verdienen Sie Geld',
      basicInfo: 'Grundinformationen',
      garageName: 'Garagenname',
      namePlaceholder: 'z. B. Sicheres Parken im Zentrum',
      description: 'Beschreibung',
      descriptionPlaceholder: 'Beschreiben Sie Ihren Garagenplatz, Standortvorteile und besondere Merkmale...',
      location: 'Standort',
      address: 'Straßenadresse',
      addressPlaceholder: 'Musterstraße 123',
      city: 'Stadt',
      state: 'Bundesland',
      postalCode: 'Postleitzahl',
      country: 'Land',
      pricing: 'Preise & Größe',
      capacity: 'Kapazität',
      hourlyRate: 'Stundensatz (€)',
      dailyRate: 'Tagessatz (€)',
      monthlyRate: 'Monatssatz (€)',
      features: 'Ausstattung',
      selectFeatures: 'Alle zutreffenden auswählen',
      covered: 'Überdacht',
      security: '24/7 Sicherheit',
      cctv: 'Videoüberwachung',
      electricCharging: 'Elektro-Ladestation',
      carWash: 'Autowäsche',
      lighting: 'Beleuchtung',
      images: 'Fotos',
      uploadImages: 'Fotos Ihrer Garage hochladen (erforderlich, max. 5MB je Datei)',
      clickUpload: 'Zum Hochladen klicken',
      dragDrop: 'PNG, JPG, GIF bis 5MB',
      availability: 'Verfügbarkeit',
      availableNow: 'Sofort verfügbar',
      cancelButton: 'Abbrechen',
      submitButton: 'Garage inserieren',
      updateButton: 'Garage aktualisieren',
      createSuccess: 'Garage erfolgreich inseriert!',
      updateSuccess: 'Garage erfolgreich aktualisiert!',
      createError: 'Garage konnte nicht inseriert werden. Bitte erneut versuchen.',
      fillRequired: 'Bitte alle erforderlichen Felder ausfüllen',
      priceGreaterThanZero: 'Preis muss größer als 0 sein',
      uploadOneImage: 'Bitte mindestens ein Bild hochladen',
      uploadingImages: 'Bilder werden hochgeladen...',
      savingDetails: 'Garagendetails werden gespeichert...',
      uploadImageError: 'Bild-Upload fehlgeschlagen',
      listing: 'Garage wird inseriert...',
    },

    // Edit Garage
    editGarage: {
      title: 'Garage bearbeiten',
      subtitle: 'Aktualisieren Sie die Angaben Ihrer Garage',
      loadingData: 'Garagendaten werden geladen...',
      notFound: 'Garage nicht gefunden',
      noPermission: 'Keine Berechtigung, diese Garage zu bearbeiten',
      failedLoad: 'Garagendaten konnten nicht geladen werden',
      updateSuccess: 'Garage erfolgreich aktualisiert!',
      updateError: 'Aktualisierung fehlgeschlagen. Bitte erneut versuchen.',
      uploadingImages: 'Neue Bilder werden hochgeladen...',
      updatingDetails: 'Details werden aktualisiert...',
      updating: 'Garage wird aktualisiert...',
      imageError: 'Bitte nur Bilddateien auswählen',
      imageSizeError: 'Bilder müssen kleiner als 5MB sein',
    },

    // My Garages
    myGarages: {
      title: 'Meine Garagen',
      subtitle: 'Verwalten Sie Ihre Garageninserate',
      addNew: 'Neue Garage hinzufügen',
      noGarages: 'Noch keine Garagen inseriert',
      noGaragesDesc: 'Beginnen Sie zu verdienen, indem Sie Ihren ersten Garagenplatz inserieren!',
      listFirst: 'Erste Garage inserieren',
      edit: 'Bearbeiten',
      delete: 'Löschen',
      deleteConfirm: 'Garage löschen',
      deleteWarning: 'Sind Sie sicher, dass Sie löschen möchten',
      deleteSubtext: 'Diese Aktion kann nicht rückgängig gemacht werden. Alle Buchungen und Daten dieser Garage werden dauerhaft entfernt.',
      deleteSuccess: 'Garage erfolgreich gelöscht!',
      deleteError: 'Löschen fehlgeschlagen',
      availabilitySuccess: 'Verfügbarkeit aktualisiert!',
      availabilityError: 'Verfügbarkeit konnte nicht aktualisiert werden',
      loadingGarages: 'Ihre Garagen werden geladen...',
      available: 'Verfügbar',
      unavailable: 'Nicht verfügbar',
      active: 'Aktiv',
      inactive: 'Inaktiv',
      noImage: 'Kein Bild',
      price: 'Preis',
      size: 'Größe',
      features: 'Ausstattung',
      moreFeatures: 'mehr',
      views: 'Ansichten',
      bookings: 'Buchungen',
      rating: 'Bewertung',
      cancel: 'Abbrechen',
      confirmDelete: 'Ja, Garage löschen',
    },

    // My Bookings
    myBookings: {
      title: 'Meine Buchungen',
      viewAll: 'Alle',
      viewPending: 'Ausstehend',
      viewConfirmed: 'Bestätigt',
      viewCompleted: 'Abgeschlossen',
      viewCancelled: 'Storniert',
      filterAll: 'Alle',
      filterUpcoming: 'Bevorstehend',
      filterPast: 'Vergangen',
      noBookings: 'Noch keine Buchungen',
      noBookingsDesc: 'Beginnen Sie, indem Sie verfügbare Garagen durchsuchen!',
      browseGarages: 'Garagen durchsuchen',
      status: 'Status',
      pending: 'Ausstehend',
      confirmed: 'Bestätigt',
      rejected: 'Abgelehnt',
      completed: 'Abgeschlossen',
      cancelled: 'Storniert',
      bookingPeriod: 'Buchungszeitraum',
      totalPrice: 'Gesamtpreis',
      bookingType: 'Typ',
      hourly: 'Stündlich',
      daily: 'Täglich',
      monthly: 'Monatlich',
      message: 'Nachricht',
      confirm: 'Bestätigen',
      reject: 'Ablehnen',
      complete: 'Abschließen',
      cancel: 'Stornieren',
      viewGarage: 'Garage ansehen',
      cancellationNote: 'Diese Buchung wurde storniert.',
      bookedOn: 'Gebucht am',
      asRenter: 'Als Mieter',
      asOwner: 'Als Eigentümer',
      duration: 'Dauer',
      days: 'Tage',
      renter: 'Mieter',
      owner: 'Eigentümer',
      phone: 'Telefon',
      cancellationReason: 'Stornierungsgrund',
      cancelReasonPrompt: 'Bitte Grund für die Stornierung angeben:',
      rejectReasonPrompt: 'Bitte Grund für die Ablehnung angeben:',
      rejectSuccess: 'Buchung erfolgreich abgelehnt',
      completeConfirm: 'Diese Buchung als abgeschlossen markieren?',
      completeSuccess: 'Buchung als abgeschlossen markiert!',
      tableNotFound: 'Buchungstabelle nicht gefunden. Bitte führen Sie FIX_BOOKINGS_TABLE.sql im Supabase-Dashboard aus.',
    },

    // Notifications
    notifications: {
      title: 'Benachrichtigungen',
      unread: 'ungelesen',
      markAllRead: 'Alle als gelesen markieren',
      filterAll: 'Alle',
      filterUnread: 'Ungelesen',
      filterRead: 'Gelesen',
      noNotifications: 'Keine Benachrichtigungen',
      noUnread: 'Alles gelesen! Keine ungelesenen Benachrichtigungen.',
      noRead: 'Noch keine gelesenen Benachrichtigungen.',
      defaultMessage: 'Hier erscheinen Benachrichtigungen, wenn Buchungen bestätigt oder abgelehnt werden.',
      delete: 'Löschen',
      markRead: 'Als gelesen markieren',
      justNow: 'Gerade eben',
      minutesAgo: 'Min. her',
      hoursAgo: 'Std. her',
      daysAgo: 'Tage her',
      loading: 'Benachrichtigungen werden geladen...',

      // Notification Templates
      types: {
        booking_confirmed: {
          title: 'Buchung bestätigt',
          message: 'Ihre Buchung für {garageName} wurde bestätigt!'
        },
        booking_rejected: {
          title: 'Buchung abgelehnt',
          message: 'Ihre Buchungsanfrage für {garageName} wurde abgelehnt.'
        },
        new_booking: {
          title: 'Neue Buchungsanfrage',
          message: '{userName} möchte {garageName} buchen.'
        },
        booking_cancelled: {
          title: 'Buchung storniert',
          message: 'Die Buchung für {garageName} wurde storniert.'
        },
        payment_received: {
          title: 'Zahlung erhalten',
          message: 'Sie haben ${amount} für {garageName} erhalten.'
        },
        review_received: {
          title: 'Neue Bewertung',
          message: '{userName} hat eine Bewertung für {garageName} hinterlassen.'
        }
      }
    },

    // Theme Settings
    theme: {
      title: 'Design auswählen',
      subtitle: 'Erlebnis anpassen',
      default: 'Standard',
      ocean: 'Ozeanblau',
      sunset: 'Sonnenuntergang Orange',
      forest: 'Waldgrün',
      midnight: 'Mitternacht Violett',
      rose: 'Rosenrosa',
      dark: 'Dunkler Modus',
      reset: 'Auf Standard zurücksetzen',
      close: 'Schließen',
      appearance: 'Erscheinungsbild',
      appearanceSubtitle: 'Aussehen und Verhalten von GarageShare anpassen',
      currentTheme: 'Aktuelles Design',
      themeWillUpdate: 'Ihre Designpräferenz wird automatisch gespeichert',
      chooseYourTheme: 'Design auswählen',
      themeChanged: 'Design geändert zu',
      themeChangeFailed: 'Designänderung fehlgeschlagen. Bitte erneut versuchen.',
    },

    // Common
    common: {
      save: 'Speichern',
      cancel: 'Abbrechen',
      delete: 'Löschen',
      edit: 'Bearbeiten',
      close: 'Schließen',
      confirm: 'Bestätigen',
      loading: 'Lädt...',
      error: 'Fehler',
      success: 'Erfolg',
      available: 'verfügbar',
      search: 'Suchen',
      filter: 'Filter',
      reset: 'Zurücksetzen',
      apply: 'Anwenden',
      back: 'Zurück',
      next: 'Weiter',
      submit: 'Absenden',
      update: 'Aktualisieren',
      required: 'Erforderlich',
      optional: 'Optional',
      yes: 'Ja',
      no: 'Nein',
    },

    // Map
    map: {
      viewMap: 'Kartenansicht',
      viewList: 'Listenansicht',
      yourLocation: 'Ihr Standort',
      away: 'entfernt',
      apiKeyMissing: 'Google Maps API-Schlüssel nicht konfiguriert',
      apiKeyHint: 'Bitte REACT_APP_GOOGLE_MAPS_API_KEY in die .env-Datei eintragen',
      locationError: 'Ihr Standort konnte nicht ermittelt werden. Browser-Berechtigungen prüfen.',
      selectLocation: 'Standort auf Karte wählen',
      pickerInstructions: 'Auf die Karte klicken oder Marker ziehen, um Standort zu wählen',
      useCurrentLocation: 'Aktuellen Standort verwenden',
      gettingAddress: 'Adresse wird ermittelt...',
      coordinates: 'Koordinaten',
      showNearby: 'In der Nähe anzeigen',
      distance: 'Entfernung',
      sortByDistance: 'Nach Entfernung sortieren',
    },

    // Features
    features: {
      '24/7 Access': '24/7 Zugang',
      '24/7 Security': '24/7 Sicherheit',
      'Security Camera': 'Sicherheitskamera',
      'Security System': 'Sicherheitssystem',
      'CCTV': 'Videoüberwachung',
      'Gated': 'Eingezäunt',
      'Smart Lock': 'Smartes Schloss',
      'Well-lit': 'Gut beleuchtet',
      'Lighting': 'Beleuchtung',
      'Electric Outlet': 'Steckdose',
      'Electric Charging': 'Ladestation',
      'Climate Controlled': 'Klimatisiert',
      'Covered': 'Überdacht',
      'Covered Parking': 'Überdachtes Parken',
      'Car Wash': 'Autowäsche',
      'Double Door': 'Doppeltür',
      'Workbench': 'Werkbank',
      'Storage Shelves': 'Regale',
      'Water Access': 'Wasserzugang',
      'Wi-Fi Available': 'WLAN verfügbar',
      'Valet Parking': 'Parkservice',
      'Maintenance Service': 'Wartungsservice',
      'Automated Door': 'Automatische Tür',
    },

    // Time
    time: {
      hour: 'Stunde',
      hours: 'Stunden',
      day: 'Tag',
      days: 'Tage',
      month: 'Monat',
      months: 'Monate',
      week: 'Woche',
      weeks: 'Wochen',
    },

    // Price Units
    priceUnit: {
      hour: 'Pro Stunde',
      day: 'Pro Tag',
      week: 'Pro Woche',
      month: 'Pro Monat',
    },

    // Sizes
    sizes: {
      single: 'Einzelstellplatz',
      double: 'Doppelstellplatz',
      large: 'Groß (5+ Autos)',
      rv: 'Wohnmobil/LKW',
      small: 'Klein (Kompakt)',
      medium: 'Mittel (Limousine)',
      extra_large: 'Extra groß',
    },

    // Capacity
    capacity: {
      single: '1 Auto',
      double: '2 Autos',
      large: '5+ Autos',
      rv: '1 Wohnmobil/LKW',
      small: '1 kleines Auto',
      medium: '1 Limousine',
      'extra-large': 'Mehrere Fahrzeuge',
      label: 'Kapazität',
      available: 'Verfügbar',
      full: 'Voll',
      spots: 'Plätze',
      spot: 'Platz',
    },

    // Extended Common Terms
    commonExtended: {
      more: 'mehr',
      loadMore: 'Mehr laden',
      new: 'Neu',
      booking: 'Buchung',
      bookings: 'Buchungen',
      garage: 'Garage',
      garages: 'Garagen',
      selected: 'ausgewählt',
      available: 'verfügbar',
      unavailable: 'nicht verfügbar',
      total: 'Gesamt',
      from: 'von',
      to: 'bis',
      per: 'pro',
      all: 'Alle',
      none: 'Keine',
      showing: 'Anzeigen',
      of: 'von',
      results: 'Ergebnisse',
      result: 'Ergebnis',
      views: 'Ansichten',
      rating: 'Bewertung',
    },

    // Auth Messages
    auth: {
      signInRequired: 'Zum Anzeigen der Eigentümerkontaktdaten anmelden',
      loginToBook: 'Zum Buchen anmelden',
      signInToBook: 'Melden Sie sich an, um diese Garage zu buchen',
      ownGarage: 'Das ist Ihre Garage',
    },

    // Admin Dashboard
    admin: {
      title: 'Admin-Dashboard',
      subtitle: 'Plattform-Analysen & Verwaltung',
      overview: 'Übersicht',
      statistics: 'Statistiken',
      recentActivity: 'Letzte Aktivität',
      topGarages: 'Top-Garagen',
      quickActions: 'Schnellaktionen',

      // Stats Cards
      totalGarages: 'Garagen gesamt',
      availableGarages: 'Verfügbare Garagen',
      totalUsers: 'Benutzer gesamt',
      totalBookings: 'Buchungen gesamt',
      confirmedBookings: 'Bestätigt',
      pendingBookings: 'Ausstehend',
      totalRevenue: 'Gesamtumsatz',
      monthlyStats: 'Letzte 30 Tage',
      confirmed: 'bestätigt',
      pending: 'ausstehend',
      cancelled: 'storniert',
      completed: 'abgeschlossen',
      last30Days: 'Letzte 30 Tage',
      newBookings: 'Neue Buchungen',
      newGarages: 'Neue Garagen',
      newUsers: 'Neue Benutzer',
      bookingCount: 'Buchungen',

      // Recent Bookings
      recentBookings: 'Letzte Buchungen',
      bookingId: 'Buchungs-ID',
      renter: 'Mieter',
      garage: 'Garage',
      dates: 'Daten',
      status: 'Status',
      price: 'Preis',
      noRecentBookings: 'Keine letzten Buchungen',

      // Top Garages
      topGaragesByBookings: 'Top-Garagen nach Buchungen',
      bookings: 'Buchungen',
      revenue: 'Umsatz',
      owner: 'Eigentümer',
      noTopGarages: 'Noch keine Top-Garagen',

      // Quick Actions
      viewAllUsers: 'Alle Benutzer anzeigen',
      manageGarages: 'Garagen verwalten',
      manageBookings: 'Buchungen verwalten',
      manageBugReports: 'Fehlerberichte verwalten',
      manageDisputes: 'Streitigkeiten verwalten',
      systemSettings: 'Systemeinstellungen',
      reports: 'Berichte erstellen',
      refreshData: 'Daten aktualisieren',
      
      // Bug Reports
      bugReports: 'Fehlerberichte',
      noBugReports: 'Keine Fehlerberichte',

      // Loading & Errors
      loading: 'Analysen werden geladen...',
      error: 'Analysedaten konnten nicht geladen werden',
      retry: 'Wiederholen',
    },

    // Disputes
    disputes: {
      title: 'Streitbeilegung',
      subtitle: 'Verwalten und lösen Sie Buchungskonflikte',
      noDisputes: 'Keine Streitigkeiten zu lösen',
      newDispute: 'Neuer Streit',
      viewDispute: 'Details ansehen',
      resolveDispute: 'Streit lösen',
      
      // Dispute List
      filterStatus: 'Nach Status filtern',
      filterPriority: 'Nach Priorität filtern',
      disputeId: 'Streit-ID',
      booking: 'Buchung',
      initiator: 'Eingereicht von',
      respondent: 'Antwort von',
      filed: 'Eingereicht',
      dueBy: 'Fällig am',
      
      // Status
      statusOpen: 'Offen',
      statusInReview: 'In Überprüfung',
      statusMediation: 'Vermittlung',
      statusResolved: 'Gelöst',
      statusRejected: 'Abgelehnt',
      
      // Priority
      priorityLow: 'Niedrig',
      priorityMedium: 'Mittel',
      priorityHigh: 'Hoch',
      priorityCritical: 'Kritisch',
      
      // Dispute Types
      bookingIssue: 'Buchungsproblem',
      paymentIssue: 'Zahlungsproblem',
      damageClaim: 'Schadensersatzforderung',
      communicationProblem: 'Kommunikationsproblem',
      conditionMismatch: 'Zustandsabweichung',
      accessIssue: 'Zugriffsproblem',
      other: 'Sonstiges',
      
      // Details
      title: 'Streit-Titel',
      description: 'Beschreibung',
      respondentResponse: 'Antwort des Respondenten',
      evidence: 'Beweise und Anhänge',
      adminNotes: 'Administrator Notizen',
      
      // Actions
      markInReview: 'Als in Überprüfung markieren',
      requestMediation: 'Vermittlung anfordern',
      approve: 'Genehmigen',
      reject: 'Ablehnen',
      offerCompensation: 'Entschädigung anbieten',
      initiateRefund: 'Rückerstattung einleiten',
      close: 'Streit schließen',
      reassign: 'Administrator neu zuweisen',
      
      // Resolution
      resolutionStatus: 'Lösungsstatus',
      resolutionApproved: 'Genehmigt',
      resolutionRejected: 'Abgelehnt',
      refundInitiated: 'Rückerstattung eingeleitet',
      compensationApproved: 'Entschädigung genehmigt',
      resolutionAmount: 'Lösungsbetrag',
      resolutionNotes: 'Lösungsnotizen',
      
      // Form
      addAdminNotes: 'Administratornotizen hinzufügen...',
      selectResolution: 'Lösungstyp wählen',
      enterAmount: 'Betrag eingeben',
      fileEvidence: 'Beweise hochladen',
      addNote: 'Notiz hinzufügen',
      
      // Messages
      disputeCreated: 'Streit erfolgreich erstellt',
      disputeResolved: 'Streit erfolgreich gelöst',
      disputeAssigned: 'Streit wurde Ihnen zugewiesen',
      pendingResponse: 'Warte auf Antwort',
      responseReceived: 'Der Respondent hat geantwortet',
      
      // Timestamps
      createdAt: 'Erstellt',
      resolvedAt: 'Gelöst',
      daysOpen: 'Tage offen',
    },

    // File Dispute
    fileDispute: {
      title: 'Beschwerde Einreichen',
      subtitle: 'Beschreiben Sie Ihr Buchungsproblem zur Lösung',
      disputeType: 'Art der Beschwerde',
      priority: 'Prioritätsstufe',
      titleLabel: 'Titel der Beschwerde',
      titlePlaceholder: 'Kurzer Titel des Problems (5-100 Zeichen)',
      titleRequired: 'Bitte geben Sie einen Beschwerde-Titel ein',
      titleTooShort: 'Der Titel muss mindestens 5 Zeichen lang sein',
      descriptionLabel: 'Detaillierte Beschreibung',
      descriptionPlaceholder: 'Erklären Sie, was passiert ist und warum Sie diese Beschwerde einreichen. Geben Sie Daten, Zeiten und relevante Details an (mind. 20 Zeichen)',
      descriptionRequired: 'Bitte geben Sie eine ausführliche Beschreibung ein',
      descriptionTooShort: 'Die Beschreibung muss mindestens 20 Zeichen lang sein',
      info: 'Ihre Beschwerde wird von unserem Admin-Team überprüft. Eine faire Lösung wird anhand von Beweisen und Plattformrichtlinien festgelegt.',
      filDispute: 'Beschwerde Einreichen',
      filing: 'Wird eingereicht...',
      disputeFiled: 'Beschwerde erfolgreich eingereicht. Ein Admin wird sie bald überprüfen.',
    },

    // Bug Report
    bugReport: {
      title: 'Fehler Melden',
      subtitle: 'Helfen Sie uns, indem Sie technische Probleme melden',
      category: 'Kategorie',
      categoryGeneral: 'Allgemein',
      categoryUI: 'Benutzeroberfläche',
      categoryBooking: 'Buchung',
      categoryPayment: 'Zahlung',
      categorySearch: 'Suche',
      categoryMap: 'Karte',
      categoryNotification: 'Benachrichtigungen',
      categoryAuthentication: 'Authentifizierung',
      categoryPerformance: 'Leistung',
      categoryMobile: 'Mobil',
      categoryOther: 'Sonstiges',
      severity: 'Schweregrad',
      severityLow: 'Niedrig (Geringfügiges Problem)',
      severityMedium: 'Mittel (Funktion funktioniert nicht)',
      severityHigh: 'Hoch (Hauptfunktionalität fehlerhaft)',
      severityCritical: 'Kritisch (App nicht nutzbar)',
      titleLabel: 'Fehler-Titel',
      titlePlaceholder: 'Kurzbeschreibung des Fehlers (5-100 Zeichen)',
      titleRequired: 'Bitte geben Sie einen Fehler-Titel ein',
      titleTooShort: 'Der Titel muss mindestens 5 Zeichen lang sein',
      descriptionLabel: 'Detaillierte Beschreibung',
      descriptionPlaceholder: 'Beschreiben Sie, was Sie getan haben, was schiefgelaufen ist und was Sie erwartet haben. Geben Sie Schritte zur Reproduktion an, falls möglich (mind. 20 Zeichen)',
      descriptionRequired: 'Bitte geben Sie eine ausführliche Beschreibung ein',
      descriptionTooShort: 'Die Beschreibung muss mindestens 20 Zeichen lang sein',
      pageLabel: 'Seite/Funktion (Optional)',
      pagePlaceholder: 'z.B. /garage/123, Buchungsformular, Suchseite',
      pageHint: 'Helfen Sie uns, zu identifizieren, wo der Fehler aufgetreten ist',
      info: 'Ihr Fehlerbericht hilft uns, GarageShare zu verbessern. Unser Team wird ihn überprüfen und an einem Fix arbeiten. Vielen Dank für Ihr Feedback!',
      submitReport: 'Bericht Einreichen',
      submitting: 'Wird eingereicht...',
      reportedSuccessfully: 'Fehlerbericht erfolgreich eingereicht. Vielen Dank, dass Sie uns helfen, zu verbessern!',
    },

    // Bug Report Admin
    bugReportAdmin: {
      title: 'Fehlerberichtverwaltung',
      subtitle: 'Überprüfen und verwalten Sie alle Fehlerberichte von Benutzern',
      backToDashboard: 'Zurück zum Dashboard',
      totalReports: 'Gesamt Berichte',
      statusOpen: 'Offen',
      statusInProgress: 'In Bearbeitung',
      statusResolved: 'Gelöst',
      statusClosed: 'Geschlossen',
      statusWontFix: 'Wird nicht behoben',
      filterStatus: 'Status:',
      filterSeverity: 'Schweregrad:',
      filterAll: 'Alle',
      allStatus: 'Alle Status',
      allSeverity: 'Alle Schweregrade',
      refresh: 'Aktualisieren',
      noReports: 'Keine Fehlerberichte gefunden',
      loading: 'Fehlerberichte werden geladen...',
      category: 'Kategorie',
      page: 'Seite',
      browser: 'Browser',
      viewDetails: 'Details Ansehen',
      startWorking: 'Bearbeitung Starten',
      markResolved: 'Als Gelöst Markieren',
      close: 'Schließen',
      wontFix: 'Wird nicht behoben',
      delete: 'Löschen',
      deleteConfirm: 'Sind Sie sicher, dass Sie diesen Fehlerbericht löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
      statusUpdated: 'Fehlerbericht markiert als',
      deleted: 'Fehlerbericht erfolgreich gelöscht',
      loadFailed: 'Fehlerberichte konnten nicht geladen werden',
      updateFailed: 'Status konnte nicht aktualisiert werden',
      deleteFailed: 'Fehlerbericht konnte nicht gelöscht werden',
      description: 'Beschreibung',
      pageUrl: 'Seiten-URL',
      browserInfo: 'Browser-Informationen',
      userInfo: 'Benutzer-Informationen',
      userName: 'Name',
      userEmail: 'E-Mail',
      timeline: 'Zeitlinie',
      created: 'Erstellt',
      updated: 'Aktualisiert',
      adminNotes: 'Admin-Notizen',
      notesPlaceholder: 'Interne Notizen zu diesem Fehlerbericht hinzufügen...',
      saveNote: 'Notiz Speichern',
      anonymous: 'Anonym',
    },
  },
};

export default translations;
